import { useMemo } from "react";

import type { SavingsSummaryResponse } from "@/app/(general-header)/botb-rewards/types";
import { useGetSubscriptionPlanQuery } from "@/app/redux/features/api/common";
import { useGetSavingsSummaryQuery } from "@/app/redux/features/api/membersHome";
import ActiveMemberBadge from "./ActiveMemberBadge";
import SavingsWidget from "./SavingsWidget";

const USE_MOCKS = false;

const MOCK_SAVINGS: SavingsSummaryResponse = {
  LifetimeTotal: 847.5,
  LifetimePassSavings: 612.0,
  LifetimeCashback: 135.5,
  LifetimeRewardsPerks: 100.0,
  CurrentPeriodTotal: 214.75,
  CurrentPeriodPass: 164.75,
  CurrentPeriodCashback: 30.0,
  CurrentPeriodRewardsPerks: 20.0,
  CurrentPeriodStart: "10 Mar 26",
  CurrentPeriodEnd: "10 Apr 26",
  CurrentTier: null,
  CurrentPlanType: null,
  IsCashbackActive: true,
  TotalCompetitionsParticipated: 42,
  FirstName: "John",
  Surname: "Doe",
  MemberSince: 1710000000,
};

function formatMemberSince(unixSeconds: number): string {
  const date = new Date(unixSeconds * 1000);
  const day = date.getDate();
  const month = date.toLocaleString("en-GB", { month: "short" });
  const year = String(date.getFullYear()).slice(-2);
  return `${day} ${month} ${year}`;
}

export default function MemberCard() {
  const { data: subscriptionPlan, isLoading: isSubLoading } = useGetSubscriptionPlanQuery();
  const isSubscriber = !!subscriptionPlan?.status && subscriptionPlan.status !== "";

  const { data: apiSavings, isLoading: isSavingsLoading } = useGetSavingsSummaryQuery(undefined, {
    skip: !isSubscriber || isSubLoading,
  });
  const savings = USE_MOCKS ? MOCK_SAVINGS : apiSavings;
  const isLoading = isSubLoading || isSavingsLoading;

  const planLabel = isLoading ? "Premium Plan" : subscriptionPlan?.name ? `${subscriptionPlan.name} Plan` : "";
  const memberSince = useMemo(
    () => (savings?.MemberSince ? formatMemberSince(savings.MemberSince) : ""),
    [savings?.MemberSince]
  );
  const displayName = isLoading
    ? "First Last"
    : savings?.FirstName && savings?.Surname
      ? `${savings.FirstName} ${savings.Surname}`
      : "";
  const currentPeriod =
    savings?.CurrentPeriodStart && savings?.CurrentPeriodEnd
      ? `${savings.CurrentPeriodStart} - ${savings.CurrentPeriodEnd}`
      : "";

  return (
    <div className="relative bg-white rounded-xl overflow-hidden px-4 lg:px-10 py-5 lg:py-8 flex flex-col gap-4 lg:gap-6 items-center w-full max-w-[576px] shadow-[0px_4px_30px_0px_rgba(36,0,67,0.2)]">
      <img
        src="/assets/images/botb-pass-members/party-emoji.png"
        alt=""
        className="absolute -top-[27px] lg:-top-[23px] -right-6 size-[82px] lg:size-28 pointer-events-none"
      />
      <img
        src="/assets/images/botb-pass-members/coins.png"
        alt=""
        className="absolute bottom-[37px] lg:bottom-[49px] -left-[27px] lg:-left-[42px] size-[121px] lg:size-[186px] pointer-events-none object-cover"
      />

      <div className={"flex flex-col gap-4 lg:gap-6 items-center w-full"}>
        <div className="flex flex-col gap-[6px] lg:gap-2 items-center">
          <div className="flex flex-col gap-1 lg:gap-2 items-center">
            <ActiveMemberBadge />
            <div
              className={`text-dark-1 text-[22px] lg:text-2xl font-bold font-jost leading-7 lg:leading-7.5 text-center
              ${isLoading ? "blur-[5px] opacity-30" : ""}`}>
              {displayName}
            </div>
          </div>
          <div className={`flex items-center gap-1 lg:gap-2 ${isLoading ? "blur-[5px] opacity-30" : ""}`}>
            <span className="text-gray-1 text-[11px] lg:text-[13px] font-medium font-jost leading-3.5 lg:leading-4.5 whitespace-nowrap">
              {planLabel}
            </span>
            <div className="size-[5px] lg:size-1.5 rounded-full bg-gray-1 shrink-0" />
            <span className="text-gray-1 text-[11px] lg:text-[13px] font-medium font-jost leading-3.5 lg:leading-4.5 whitespace-nowrap">
              Member since {isLoading ? "1 Jan 25" : memberSince}
            </span>
          </div>
        </div>

        <div className="relative w-full">
          <SavingsWidget
            totalSavings={isLoading ? 1 : (savings?.LifetimeTotal ?? 0)}
            currentSavings={savings?.CurrentPeriodTotal ?? 0}
            currentPeriod={isLoading ? "1 Jan - 1 Feb" : currentPeriod}
            loading={isLoading}
          />
        </div>
      </div>
    </div>
  );
}
